import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Row, Col, Label, Input } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PaymentReportService from "../../../services/OrderingServices/PaymentReportService";
import toast from "react-hot-toast";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  formatDate,
  formatDateTime,
  formatDateTimes,
  getTimeOnly,
} from "../../../Components/Common/FormatDate";
import { useNavigate } from "react-router-dom";

const DayBookLogs = () => {
  const [loading, setLoading] = useState(false);
  const [paymentTypeList, setPaymentTypeList] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchCreatedBy, setSearchCreatedBy] = useState("");
  const navigate = useNavigate();

  const fetchDayLog = async (start, end) => {
    setLoading(true);
    try {
      const response = await PaymentReportService.dayBookLog(start, end);
      setPaymentTypeList(response?.items || []);
    } catch (error) {
      console.error("Error fetching payment day book:", error);
      toast.error("Failed to fetch payment report");
    } finally {
      setLoading(false);
    }
  };

  const handleReportClick = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    const formatDate = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    fetchDayLog(formatDate(startDate), formatDate(endDate));
  };

  useEffect(() => {
    // Default: last 1 month
    const today = new Date();
    const oneMonthBefore = new Date();
    oneMonthBefore.setMonth(today.getMonth() - 1);

    setStartDate(oneMonthBefore);
    setEndDate(today);

    const formatDate = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Fetch last 1 month data on page load
    fetchDayLog(formatDate(oneMonthBefore), formatDate(today));
  }, []);

  const filteredList = paymentTypeList.filter((item) =>
    item.createdBy.toLowerCase().includes(searchCreatedBy.toLowerCase())
  );

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Day Book Logs" pageTitle="Logs" />

        {/* Filter Card */}
        <Card className="mb-3 shadow-sm">
          <CardBody>
            <Row className="align-items-end g-2">
              <Col>
                {" "}
                <Label className="mb-1">Created By</Label>
                <Input
                  placeholder="Search by Created by"
                  value={searchCreatedBy}
                  onChange={(e) => setSearchCreatedBy(e.target.value)}
                />
              </Col>
              <Col xs={6} lg={3}>
                <Label className="mb-1">Start Date</Label>
                <Flatpickr
                  value={startDate}
                  onChange={([date]) => setStartDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    maxDate: new Date(),
                  }}
                  className="form-control form-control-sm"
                  style={{ height: "42px" }}
                />
              </Col>
              <Col xs={6} lg={3}>
                <Label className="mb-1">End Date</Label>
                <Flatpickr
                  value={endDate}
                  onChange={([date]) => setEndDate(date)}
                  options={{
                    dateFormat: "d/m/Y",
                    minDate: startDate,
                    maxDate: new Date(),
                  }}
                  className="form-control form-control-sm"
                  style={{ height: "42px" }}
                />
              </Col>
              <Col xs={12} lg={3} className="text-end mt-2 mt-lg-0">
                <Button
                  color="success"
                  onClick={handleReportClick}
                  disabled={loading}
                >
                  Generate Report
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Desktop Table */}
        <Card className="mb-3 shadow-sm">
          <CardBody>
            <div className="d-lg-block d-none table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "80px" }}>S.N</th>
                    <th>Closed By</th>
                    <th className="text-center">Closed Date</th>
                    <th className="text-center">Closed Time</th>
                    <th className="text-end">Total Amount (Rs.)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList?.map((item, index) => (
                    <tr key={item.paymentTypeId}>
                      <td>{index + 1}</td>
                      <td>{item.createdBy}</td>
                      <td className="text-center">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="text-center">
                        {getTimeOnly(item.createdAt)}
                      </td>
                      <td className="text-end">
                        {item.totalAmount.toFixed(2)}
                      </td>
                      <td className="d-flex justify-content-center">
                        <Button
                          color="btn btn-soft-success"
                          className="btn-sm"
                          onClick={() => navigate(`/logs/day-book/${item?.id}`)}
                        >
                          <i className="bx bx-show" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredList.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No day book logs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        {/* Mobile Cards */}
        <div className="d-block d-lg-none">
          {loading ? (
            <div className="d-flex justify-content-center my-4">
              <Triangle visible={true} height="60" width="60" color="#5B71B9" />
            </div>
          ) : filteredList.length > 0 ? (
            filteredList.map((item, index) => (
              <Card key={item.paymentTypeId} className="mb-2 shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{item.createdBy}</strong>
                    <div className="text-muted small mt-1">
                      S.N: {index + 1}
                    </div>
                  </div>
                  <div className="fw-bold">
                    Rs. {item.totalAmount.toFixed(2)}
                  </div>
                </CardBody>
              </Card>
            ))
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-0">No day book logs found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DayBookLogs;
